import React, { useMemo } from "react";
import ReactHtmlParser from "html-react-parser";
import Popup from "reactjs-popup";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import { IEmail, IFile } from "../../../store/types";
import { EmailFilePreview } from "../../Email-box/Email-file-preview/EmailFilePreview";
import { useNavigationHooks, useUrlHooks } from "../../../hooks";
import { Avatar, Box, Container, Divider, ListItemText, Stack, Typography } from "@mui/material";

interface EmailViewProps {
  email: IEmail;
  isOpen: boolean;
  onClose: () => void;
}

export const EmailView: React.FC<EmailViewProps> = ({ email, isOpen, onClose }) => {
  const { removeParams } = useNavigationHooks();
  const { queryParams } = useUrlHooks();
  const { emailBox } = queryParams;

  const renderFiles = useMemo(() => {
    return email.attachments?.map((file: IFile, index) => {
      return <EmailFilePreview key={index} file={file} />;
    });
  }, [email.attachments]);

  const formattedDate = Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    day: "numeric",
    month: "short"
  }).format(new Date(email.created_at));

  const emailSender = useMemo(() => {
    if (!email.sender_name.length) return;
    if (email.sender_name.trim().indexOf(" ") !== -1) {
      //there is at least one space, excluding leading and training spaces
      return `${email.sender_name.split(" ")[0][0]}${email.sender_name.split(" ")[1][0]}`;
    } else return `${email.sender_name.split("")[0][0]}`;
  }, [email.sender_name]);

  return (
    <Popup
      modal
      open={isOpen}
      nested
      onClose={onClose}
      overlayStyle={{
        background: "rgba(0, 0, 0, 0.5)"
      }}
    >
      <Container
        disableGutters
        sx={{
          backgroundColor: "#ffffff",
          borderRadius: "10px",
          minWidth: "555px",
          maxWidth: "1000px",
          padding: "30px",
          display: "flex",
          flexDirection: "column",
          gap: "20px"
        }}
        maxWidth={false}
      >
        <Box>
          <Stack flexDirection="row" alignItems="center" justifyContent="space-between" padding="7px 0px">
            <Typography>Email preview</Typography>
            <CloseIcon
              sx={{ cursor: "pointer" }}
              onClick={() => {
                if (emailBox) {
                  removeParams(["emailBox"]);
                }
                onClose();
              }}
            />
          </Stack>
          <Divider sx={{ backgroundColor: "#DAE0E2" }} />
        </Box>
        <Box>
          <Stack direction="row" gap="6px">
            <Avatar
              sx={{ backgroundColor: "#3a5b9f", width: 30, height: 30, fontSize: "10px" }}
              alt={email.sender_name}
              src={"/static/images/avatar/11.jpg"}
            >
              {emailSender}
            </Avatar>
            <ListItemText
              sx={{ margin: 0, maxHeight: "30px" }}
              primary={email.sender_name}
              secondary={email.email_subject}
              primaryTypographyProps={{ sx: { fontWeight: 400, lineHeight: 1, color: "#0A3142" } }}
              secondaryTypographyProps={{ sx: { fontSize: "13px" }, color: "secondary" }}
            />
            <Stack alignItems="flex-end" maxHeight="30px">
              <Typography variant="body2" color="#5D7782">
                {formattedDate}
              </Typography>
              {!!email.attachments.length && (
                <AttachFileIcon sx={{ transform: "rotate(45deg)", fontSize: 20, color: "#5D7782" }} />
              )}
            </Stack>
          </Stack>
        </Box>
        <Box>
          <Stack gap="10px">
            {email.email_body && (
              <Typography
                color="#0A3142"
                lineHeight="1"
                overflow="auto"
                maxHeight="600px"
                sx={{
                  "::-webkit-scrollbar-thumb": {
                    backgroundColor: "#DAE0E2"
                  }
                }}
              >
                {ReactHtmlParser(email.email_body)}
              </Typography>
            )}
            <Box display="flex" flexWrap="wrap" gap="15px" sx={{ padding: "10p", color: "#0a3142", maxWidth: "500px" }}>
              {renderFiles}
            </Box>
          </Stack>
        </Box>
      </Container>
    </Popup>
  );
};
